import React from 'react';
import { Link } from 'react-router-dom';
import { useSettings } from '../../context/SettingsContext';
import { renderSettingText } from '../../lib/textTemplate';
import { resolveApiAssetUrl } from '../../lib/api';
import SponsorshipStrip from '../../components/SponsorshipStrip';
import EntryDomainStrip from '../../components/EntryDomainStrip';
import { SHELL_IDS } from '../../lib/domHooks';

/**
 * Betrun footer'ı — referans tasarımdaki iki bölümlü düzen.
 *
 * Solda panelden gelen menü grupları başlıklı sütunlar olarak dizilir
 * (Footer Yönetimi → menü grupları); sağda iki blok durur: "Düzenlemeler ve
 * İş Ortakları" (lisans rozetleri + lisans metni) ve "Yatırım Yöntemleri"
 * (ödeme logoları). İçeriğin tamamı ayar kaydından gelir; boş olan blok hiç
 * çizilmez. Bir süre footer tek satırlık başlıksız bir bağlantı dizisiydi ve
 * operatör onu yetersiz buldu (lisans metni ve menü başlıkları görünmüyordu).
 *
 * Sosyal ikonlar bu footer'da yoktur: Telegram ve canlı destek sol menünün
 * altındaki iletişim kutusundadır (BetrunLayout).
 */
const FooterLink = ({ item, settings }) => {
  const label = renderSettingText(item.label, settings);
  if ((item.url || '').startsWith('/') && !item.new_tab) {
    return <Link to={item.url} className="betrun-foot-link">{label}</Link>;
  }
  return (
    <a
      href={item.url || '#'}
      target={item.new_tab ? '_blank' : undefined}
      rel={item.new_tab ? 'noreferrer' : undefined}
      className="betrun-foot-link"
    >
      {label}
    </a>
  );
};

const LogoTile = ({ item, image, label, className }) => {
  const content = (
    <>
      {image && <img src={resolveApiAssetUrl(image)} alt={label} loading="lazy" />}
      {!image && label && <span className="betrun-foot-tile-label">{label}</span>}
    </>
  );
  return item.link ? (
    <a
      href={item.link}
      target={item.new_tab ? '_blank' : undefined}
      rel={item.new_tab ? 'noreferrer' : undefined}
      className={className}
      aria-label={label}
      title={label}
    >
      {content}
    </a>
  ) : (
    <span className={className} title={label}>{content}</span>
  );
};

const BetrunFooter = () => {
  const settings = useSettings();
  // Bağlantısı olmayan grup sütun olarak çizilmez: başlığın altı boş kalırdı.
  const menuGroups = (settings.footer_menu_groups || [])
    .map((group) => ({
      ...group,
      links: (group.links || []).filter((item) => item.label),
    }))
    .filter((group) => group.links.length > 0);
  const licenses = (settings.footer_licenses || []).filter((item) => item.image_url);
  const licenseText = renderSettingText(settings.footer_license_text || '', settings).trim();
  // Logosu ve etiketi birden boş olan kayıt çizilmez.
  const paymentMethods = (settings.footer_payment_methods || [])
    .filter((item) => item.image_url || item.label);
  const sponsors = (settings.footer_sponsors || []).filter((item) => item.image_url);
  const paymentTitle = renderSettingText(
    settings.footer_payment_title || 'Yatırım Yöntemleri', settings,
  );
  const sponsorTitle = renderSettingText(
    settings.footer_sponsor_title || 'Sponsorlar', settings,
  );
  const hasRegulation = licenses.length > 0 || licenseText;
  const hasSide = hasRegulation || paymentMethods.length > 0 || sponsors.length > 0;

  return (
    <footer id={SHELL_IDS.footer} className="betrun-footer betrun-foot">
      {(menuGroups.length > 0 || hasSide) && (
        <div className="betrun-foot-inner">
          {menuGroups.length > 0 && (
            <nav className="betrun-foot-menus" aria-label="Alt menü">
              {menuGroups.map((group, groupIndex) => (
                <div key={`${group.title}-${groupIndex}`} className="betrun-foot-col">
                  {group.title && (
                    <h2 className="betrun-foot-heading">
                      {renderSettingText(group.title, settings)}
                    </h2>
                  )}
                  <ul>
                    {group.links.map((item, index) => (
                      <li key={`${item.label}-${index}`}>
                        <FooterLink item={item} settings={settings} />
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </nav>
          )}

          {hasSide && (
            <div className="betrun-foot-side">
              {hasRegulation && (
                <section className="betrun-foot-block" aria-labelledby="betrun-foot-regulation">
                  <h2 id="betrun-foot-regulation" className="betrun-foot-label">
                    Düzenlemeler ve İş Ortakları
                  </h2>
                  <div className="betrun-foot-regulation">
                    {licenses.length > 0 && (
                      <div className="betrun-foot-badges">
                        {licenses.map((license, index) => (
                          <LogoTile
                            key={`${license.name}-${index}`}
                            item={license}
                            image={license.image_url}
                            label={renderSettingText(license.name || 'Lisans', settings)}
                            className="betrun-foot-badge"
                          />
                        ))}
                      </div>
                    )}
                    {licenseText && <p className="betrun-foot-license-text">{licenseText}</p>}
                  </div>
                </section>
              )}

              {paymentMethods.length > 0 && (
                <section className="betrun-foot-block" aria-labelledby="betrun-foot-payments">
                  <h2 id="betrun-foot-payments" className="betrun-foot-label">{paymentTitle}</h2>
                  <div className="betrun-foot-box">
                    {paymentMethods.map((method, index) => (
                      <LogoTile
                        key={`${method.label}-${method.image_url}-${index}`}
                        item={method}
                        image={method.image_url}
                        label={renderSettingText(method.label || '', settings) || 'Yatırım yöntemi'}
                        className="betrun-foot-tile"
                      />
                    ))}
                  </div>
                </section>
              )}

              {/* Sponsor logoları panelde tanımlıysa yatırım yöntemlerinin
                  altında aynı kutu biçiminde durur; kayıt yoksa çizilmez. */}
              {sponsors.length > 0 && (
                <section className="betrun-foot-block" aria-labelledby="betrun-foot-sponsors">
                  <h2 id="betrun-foot-sponsors" className="betrun-foot-label">{sponsorTitle}</h2>
                  <div className="betrun-foot-box">
                    {sponsors.map((sponsor, index) => (
                      <LogoTile
                        key={`${sponsor.name}-${sponsor.image_url}-${index}`}
                        item={sponsor}
                        image={sponsor.image_url}
                        label={renderSettingText(sponsor.name || 'Sponsor', settings)}
                        className="betrun-foot-tile"
                      />
                    ))}
                  </div>
                </section>
              )}
            </div>
          )}
        </div>
      )}
      {/* Sponsorluk bandı footer'ın en altındadır ve bütün kabuklarda aynı
          bileşendir; kayıt yoksa hiçbir şey çizmez. */}
      <EntryDomainStrip />
      <SponsorshipStrip />
    </footer>
  );
};

export default BetrunFooter;
